/**
 * Users service layer.
 * Abstracts all Firestore operations for user profiles from the rest of the application.
 */
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  query,
  collection,
  where,
  getDocs,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const USERS_COLLECTION = 'users';

/**
 * Create a new user profile document in Firestore.
 * Uses the Firebase Auth UID as the Firestore document ID.
 * @param {string} userId - The Firebase Auth UID for the user.
 * @param {Object} profileData - The user profile data.
 * @param {string} profileData.displayName - The user's display name.
 * @param {string} profileData.username - The user's unique username.
 * @param {string} profileData.email - The user's email address.
 * @param {string} [profileData.birthdate] - The user's date of birth.
 * @param {string} [profileData.gender] - The user's gender.
 * @param {string} [profileData.avatarUrl] - URL to the user's avatar image.
 * @param {string} [profileData.bio] - The user's bio text.
 * @returns {Promise<Object>} The created user profile object.
 */
export const createUserProfile = async (userId, profileData) => {
  try {
    const userDoc = {
      displayName: profileData.displayName || '',
      username: profileData.username || '',
      email: profileData.email || '',
      bio: profileData.bio || '',
      avatarUrl: profileData.avatarUrl || '',
      birthdate: profileData.birthdate || null,
      gender: profileData.gender || '',
      interests: profileData.interests || [],
      onboardingCompleted: false,
      createdAt: serverTimestamp(),
      followersCount: 0,
      followingCount: 0,
      postsCount: 0,
    };

    const docRef = doc(db, USERS_COLLECTION, userId);
    await setDoc(docRef, userDoc);

    return { id: userId, ...userDoc, createdAt: new Date() };
  } catch (error) {
    handleServiceError(error, 'Create user profile', 'Failed to create user profile. Please try again.');
  }
};

/**
 * Retrieve a user profile by their user ID.
 * @param {string} userId - The Firebase Auth UID of the user.
 * @returns {Promise<Object|null>} The user profile object or null if not found.
 */
export const getUserProfile = async (userId) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    }

    return null;
  } catch (error) {
    handleServiceError(error, 'Get user profile', 'Failed to load user profile. Please try again.');
  }
};

/**
 * Update an existing user profile in Firestore.
 * @param {string} userId - The Firebase Auth UID of the user.
 * @param {Object} updates - The fields to update.
 * @returns {Promise<Object>} The updated user profile object.
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId);
    const updateData = {
      ...updates,
      updatedAt: serverTimestamp(),
    };

    await updateDoc(docRef, updateData);

    const updatedDoc = await getDoc(docRef);
    return { id: updatedDoc.id, ...updatedDoc.data() };
  } catch (error) {
    handleServiceError(error, 'Update user profile', 'Failed to update user profile. Please try again.');
  }
};

/**
 * Check if a username is already taken in the Firestore users collection.
 * @param {string} username - The username to check.
 * @returns {Promise<boolean>} True if the username is available, false if taken.
 */
export const isUsernameAvailable = async (username) => {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where('username', '==', username.toLowerCase()));
    const querySnapshot = await getDocs(q);

    return querySnapshot.empty;
  } catch (error) {
    handleServiceError(error, 'Username check', 'Failed to verify username availability. Please try again.');
  }
};

/**
 * Retrieve a user profile by their username.
 * @param {string} username - The username to look up.
 * @returns {Promise<Object|null>} The user profile object or null if not found.
 */
export const getUserByUsername = async (username) => {
  try {
    const usersRef = collection(db, USERS_COLLECTION);
    const q = query(usersRef, where('username', '==', username.toLowerCase()));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const docSnap = querySnapshot.docs[0];
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    handleServiceError(error, 'Get user by username', 'Failed to load user profile. Please try again.');
  }
};

/**
 * Search users by display name or username using Firestore prefix matching.
 * Searches username field with >= and <= '\uf8ff' for prefix matching.
 * @param {string} searchQuery - The search query string.
 * @param {number} [resultLimit=10] - Max number of results.
 * @returns {Promise<Array<Object>>} Array of matching user profile objects.
 */
export const searchUsers = async (searchQuery, resultLimit = 10) => {
  try {
    const normalizedQuery = searchQuery.toLowerCase().trim();
    if (!normalizedQuery) return [];

    const usersRef = collection(db, USERS_COLLECTION);

    // Search by username prefix
    const usernameQuery = query(
      usersRef,
      where('username', '>=', normalizedQuery),
      where('username', '<=', normalizedQuery + '\uf8ff'),
      firestoreLimit(resultLimit)
    );

    const usernameSnapshot = await getDocs(usernameQuery);
    const results = new Map();

    usernameSnapshot.docs.forEach((docSnap) => {
      results.set(docSnap.id, { id: docSnap.id, ...docSnap.data() });
    });

    // Also search by displayName prefix (lowercased field not available, so use display name query)
    // For display name search, we query and filter client-side for simplicity
    if (results.size < resultLimit) {
      const allUsersQuery = query(
        usersRef,
        orderBy('displayName'),
        firestoreLimit(50)
      );
      const allUsersSnapshot = await getDocs(allUsersQuery);

      allUsersSnapshot.docs.forEach((docSnap) => {
        if (results.size >= resultLimit) return;
        const data = docSnap.data();
        const displayNameLower = (data.displayName || '').toLowerCase();
        if (displayNameLower.includes(normalizedQuery) && !results.has(docSnap.id)) {
          results.set(docSnap.id, { id: docSnap.id, ...data });
        }
      });
    }

    return Array.from(results.values()).slice(0, resultLimit);
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Search users]', error);
    }
    return [];
  }
};

/**
 * Set user online status.
 * @param {string} userId
 * @param {boolean} isOnline
 * @returns {Promise<void>}
 */
export const setOnlineStatus = async (userId, isOnline) => {
  try {
    const docRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(docRef, {
      isOnline,
      lastSeen: serverTimestamp(),
    });
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      console.error('[Set online status]', error);
    }
  }
};

/**
 * Listen to a user's online status in real-time.
 * @param {string} userId
 * @param {function} callback - Called with { isOnline: boolean, lastSeen: string|null }.
 * @returns {function} Unsubscribe function.
 */
export const onUserStatusChange = (userId, callback) => {
  const docRef = doc(db, USERS_COLLECTION, userId);

  return onSnapshot(
    docRef,
    (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        callback({
          isOnline: data.isOnline || false,
          lastSeen: data.lastSeen?.toDate?.()?.toISOString() || null,
        });
      } else {
        callback({ isOnline: false, lastSeen: null });
      }
    },
    (error) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('[User status listener]', error);
      }
      callback({ isOnline: false, lastSeen: null });
    }
  );
};
